import dayjs from 'dayjs';
import Commons from '../../../../Services/Common/Common';

/**
 * 로그인 사용자 ID (복호화 가능하면 평문, 아니면 원문 trim)
 * GetSamBasic02R4 등 @UserId 비교와 동일 기준
 */
function GetLoginUserIdPlain(userInfo) {
  const enc = userInfo?.userid ?? userInfo?.userId ?? '';
  if (enc == null || enc === '') return '';
  try {
    const d = Commons.DecryptoAES(enc);
    return String(d ?? '').trim();
  } catch {
    return String(enc).trim();
  }
}

/** 행의 ID 값을 평문으로 맞춰 로그인 ID와 비교 (export: 신청자·작성자 판별) */
export function IdsMatch(rowId, userInfo) {
  const me = GetLoginUserIdPlain(userInfo);
  if (!me) return false;
  const raw = rowId;
  if (raw == null || raw === '') return false;
  const s = String(raw).trim();
  if (!s) return false;
  if (s === me) return true;
  try {
    const d = Commons.DecryptoAES(s);
    return String(d ?? '').trim() === me;
  } catch {
    return false;
  }
}

/** 해당 허가의 안전관리자(SM_ID)가 현재 로그인 사용자인지 */
function IsRowSmUser(row, userInfo) {
  return IdsMatch(row?.smId ?? row?.SmId ?? row?.SM_ID, userInfo);
}

/** 조회 API에서 내려준 '현재 로그인 사용자가 이 건에 결재 서명했는지' (TB_BO_PERMIT_DEPARTMENT_SIGN, @UserId 기준) */
function IsCurrentUserDepSigned(row) {
  return (
    String(
      row?.currentUserDepSigned ?? row?.CurrentUserDepSigned ?? ''
    ).toUpperCase() === 'Y'
  );
}

/**
 * API가 currentUserDepSigned 값을 내려준 경우에만 "본인 부서 결재 라인에 포함됨"으로 본다.
 * (값이 없으면 부서 결재 판단 불가 → 승인 Gubun/폴백에서 제외)
 */
function HasCurrentUserDepSignField(row) {
  const v = row?.currentUserDepSigned ?? row?.CurrentUserDepSigned;
  if (v === undefined || v === null) return false;
  return String(v).trim() !== '';
}

/** GetSamBasic02R4 상태 문자열 정규화 */
function NormalizePermitStatusText(statusText) {
  const s = String(statusText || '').trim();
  switch (s) {
    case '연장반려':
      return '연장반려';
    case '완료':
      return '작업완료';
    case '요청':
      return '결재요청';
    case '서명완료':
      return '결재완료';
    default:
      return s;
  }
}

/**
 * 안전관리자(SM_ID = 로그인 ID)일 때만 서명진행율 문구 반환.
 * SignedDepSignCount, TotalDepSignCount, CurrentUserDepSigned 사용.
 * @returns {string} 예: '2/4 서명완료', '2/4 서명완료 (본인서명완료)', 또는 ''
 */
export function getSignProgressForSafetyManager(row, userInfo) {
  if (!IsRowSmUser(row, userInfo)) return '';
  const total =
    Number(row?.totalDepSignCount ?? row?.TotalDepSignCount ?? 0) || 0;
  const signed =
    Number(row?.signedDepSignCount ?? row?.SignedDepSignCount ?? 0) || 0;
  if (total === 0) return '';
  const currentUserSigned = IsCurrentUserDepSigned(row);
  const base = `${signed}/${total} 서명완료`;
  return currentUserSigned ? `${base} (본인서명완료)` : base;
}

/**
 * GetSamBasic02R5_Daily Status CASE — 승인·반려 가능한 「요청」 상태
 * (결재요청·승인요청·연장요청. 입회요청·대기·결재대기 등은 제외)
 */
export const DETAIL_APPROVE_REJECT_STATUSES = new Set([
  '결재요청',
  '승인요청',
  '연장요청',
]);

/** API/그리드 Status가 승인·반려 가능한 요청 상태인지 */
export function isDetailApproveRejectStatus(statusText) {
  const normalized = NormalizePermitStatusText(String(statusText ?? '').trim());
  return DETAIL_APPROVE_REJECT_STATUSES.has(normalized);
}

/**
 * 현재 로그인 사용자가 해당 건에 대해 승인 시 사용할 Gubun (SaveSamBasic02U2)
 * - Status는 GetSamBasic02R5_Daily CASE(@UserId)와 동일 문자열 기준
 * - '1': 중간결재(센터) — 결재요청
 * - '2': 최종(SM) — 승인요청·연장요청
 * - 요청 상태가 아니면 null (depSign 등만으로 gubun 추론하지 않음)
 */
export function getApprovalGubunForRow(row, userInfo) {
  const listStatus = NormalizePermitStatusText(
    String(
      row?.status ?? row?.Status ?? row?.listStatus ?? row?.ListStatus ?? ''
    ).trim()
  );

  if (!isDetailApproveRejectStatus(listStatus)) {
    return null;
  }

  if (listStatus === '결재요청') return '1';
  if (listStatus === '승인요청' || listStatus === '연장요청') return '2';

  return null;
}

/**
 * GetSamBasic02R4 목록의 Status 문자열 우선.
 * SQL에서 상태·버튼 가능 여부가 @UserId로 이미 계산되므로 직책/approvalYn 사용 안 함.
 */
function GetPermitStatusFromR4List(statusText) {
  const normalizedStatus = NormalizePermitStatusText(statusText);
  /** 목록 행에서는 결재 처리 없음 — 상세 그리드에서만 반려/승인 */
  return {
    status: normalizedStatus || '대기',
    canApproveReject: false,
  };
}


/** 상세·마스터 행 병합 (일자 Status + MST SM_ID 등) */
export function MergePermitRowForApproval(masterRow = {}, detailRow = {}) {
  const listStatus = String(
    detailRow?.listStatus ??
      detailRow?.ListStatus ??
      detailRow?.status ??
      detailRow?.Status ??
      masterRow?.listStatus ??
      masterRow?.ListStatus ??
      masterRow?.status ??
      masterRow?.Status ??
      ''
  ).trim();

  return {
    ...masterRow,
    ...detailRow,
    listStatus,
    status: listStatus,
    smId:
      detailRow?.smId ??
      detailRow?.SmId ??
      masterRow?.smId ??
      masterRow?.SmId ??
      '',
    depSign:
      detailRow?.depSign ??
      detailRow?.DepSign ??
      masterRow?.depSign ??
      masterRow?.DepSign ??
      '',
    smSign:
      detailRow?.smSign ??
      detailRow?.SmSign ??
      masterRow?.smSign ??
      masterRow?.SmSign ??
      '',
    returnYn:
      detailRow?.returnYn ??
      detailRow?.ReturnYn ??
      masterRow?.returnYn ??
      masterRow?.ReturnYn ??
      '',
    currentUserDepSigned:
      detailRow?.currentUserDepSigned ?? masterRow?.currentUserDepSigned,
    signedDepSignCount:
      detailRow?.signedDepSignCount ?? masterRow?.signedDepSignCount,
    totalDepSignCount:
      detailRow?.totalDepSignCount ?? masterRow?.totalDepSignCount,
  };
}

/**
 * 세부 그리드 1행 — GetSamBasic02R5_Daily Status + Gubun
 * @returns {{ status: string, canApproveReject: boolean, gubun: string|null }}
 */
export function getDetailPermitStatusAndButton(
  detailRow,
  masterRow,
  userInfo
) {
  const merged = MergePermitRowForApproval(masterRow, detailRow);
  const normalizedStatus = NormalizePermitStatusText(
    String(merged.listStatus ?? merged.status ?? '').trim()
  );
  const gubun = getApprovalGubunForRow(merged, userInfo);
  const canApproveReject =
    isDetailApproveRejectStatus(normalizedStatus) && gubun != null;

  return {
    status: normalizedStatus || '대기',
    canApproveReject,
    gubun,
  };
}

/** 선택된 세부 행들의 공통 승인 Gubun (1|2). 단계가 섞이면 null */
export function getApprovalGubunForDetailRows(detailRows, masterRow, userInfo) {
  const gubuns = [];
  for (const row of detailRows || []) {
    const { gubun } = getDetailPermitStatusAndButton(row, masterRow, userInfo);
    if (gubun) gubuns.push(gubun);
  }
  if (gubuns.length === 0) return null;
  const unique = [...new Set(gubuns)];
  return unique.length === 1 ? unique[0] : null;
}

/** 세부 행 DAILY_CD */
export function resolveDetailDailyCd(row) {
  return String(row?.dailyCd ?? row?.DailyCd ?? row?.DAILY_CD ?? '').trim();
}

/** 세부 행 작업일자(YYYY-MM-DD) */
export function resolveDetailWorkDateYmd(row) {
  const raw = row?.workDate ?? row?.WorkDate ?? '';
  if (!raw) return '';
  if (dayjs(raw).isValid()) return dayjs(raw).format('YYYY-MM-DD');
  const s = String(raw).trim().substring(0, 10);
  return dayjs(s).isValid() ? dayjs(s).format('YYYY-MM-DD') : s;
}

/**
 * 일자 단위 묶음 키 — 화면 작업일자(YYYY-MM-DD) 기준 (동일 PM 내 일자별 DAILY_CD 구분)
 */
export function resolveDetailDayKey(row) {
  const ymd = resolveDetailWorkDateYmd(row);
  if (ymd) return `w:${ymd}`;
  const dailyCd = resolveDetailDailyCd(row);
  if (dailyCd) return `d:${dailyCd}`;
  return '';
}

/**
 * 세부 그리드: 한 행 선택/해제 시 같은 일자(DAILY/작업일) 행을 함께 선택 (모바일 일자 단위와 동일)
 */
export function syncDetailGridSelectionByDayKey(gridApi, toggledRow, isSelected) {
  if (!gridApi || !toggledRow) return;
  const dayKey = resolveDetailDayKey(toggledRow);
  if (!dayKey) return;

  gridApi.forEachNode((node) => {
    if (!node?.data) return;
    if (resolveDetailDayKey(node.data) === dayKey) {
      // ag-Grid 31+: 3번째 인자는 source ('api' — 연동 선택이 onSelectionChanged를 재귀 유발하지 않도록)
      node.setSelected(!!isSelected, false, 'api');
    }
  });
}

/** @deprecated syncDetailGridSelectionByDayKey 사용 */
export const syncDetailGridSelectionByDay = syncDetailGridSelectionByDayKey;

/** 선택 삭제 가능 상태 (GetSamBasic02R5_Daily CASE) */
const DETAIL_DELETABLE_STATUSES = new Set(['반려', '대기']);

export function isDetailDeletableStatus(statusText) {
  const normalized = NormalizePermitStatusText(String(statusText ?? '').trim());
  return DETAIL_DELETABLE_STATUSES.has(normalized);
}

/** 작업허가 신청자(작성자) = 로그인 사용자 */
export function isPermitRequestAuthor(masterRow, userInfo) {
  const authorId =
    masterRow?.reqId ??
    masterRow?.ReqId ??
    masterRow?.REQ_ID ??
    masterRow?.createId ??
    masterRow?.CreateId ??
    masterRow?.CREATE_ID ??
    '';
  return IdsMatch(authorId, userInfo);
}

/**
 * 세부 행 선택 삭제 버튼 활성화 (SAM_BASIC_02 웹 관리자)
 * - 선택된 세부 행이 있으면 활성화 (상태·신청자 제한 없음)
 * - 모바일 등: isDetailDeletableStatus + isPermitRequestAuthor 로 별도 제한
 */
export function canDetailSelectDelete(detailRows) {
  return (detailRows?.length ?? 0) > 0;
}

/** 세부 행 선택 기준 승인·반려 버튼 활성화 (모든 선택 행이 요청 상태·동일 Gubun) */
export function canDetailApproveReject(detailRows, masterRow, userInfo) {
  if (!detailRows?.length) return false;
  for (const row of detailRows) {
    const { canApproveReject } = getDetailPermitStatusAndButton(
      row,
      masterRow,
      userInfo
    );
    if (!canApproveReject) return false;
  }
  return getApprovalGubunForDetailRows(detailRows, masterRow, userInfo) != null;
}

/**
 * 안전작업허가서 처리상태 및 반려/승인 버튼 활성화 여부
 * @param {object} row - 그리드 행 (status 우선, 없으면 returnYn·depSign·smSign·smId·currentUserDepSigned 등)
 * @param {object} userInfo - 로그인 사용자 (userid / userId로 SM·부서결재 보조 판별)
 */
export function getPermitStatusAndButton(row, userInfo) {
  const listApiStatus = NormalizePermitStatusText(
    String(
      row?.status ?? row?.Status ?? row?.listStatus ?? row?.ListStatus ?? ''
    ).trim()
  );
  if (listApiStatus) {
    return GetPermitStatusFromR4List(listApiStatus);
  }

  const returnYn =
    String(
      row?.returnYn ?? row?.ReturnYn ?? row?.RETURN_YN ?? ''
    ).toUpperCase() === 'Y';
  const depSign =
    String(
      row?.depSign ?? row?.DepSign ?? row?.DEP_SIGN ?? ''
    ).toUpperCase() === 'Y';
  const smSign =
    String(row?.smSign ?? row?.SmSign ?? row?.SM_SIGN ?? '').toUpperCase() ===
    'Y';
  const workEndDate = row?.endTime ?? row?.EndTime ?? row?.END_TIME ?? '';
  const today = dayjs().format('YYYY-MM-DD');
  const endDateStr = workEndDate ? dayjs(workEndDate).format('YYYY-MM-DD') : '';

  if (returnYn) {
    return { status: '반려', canApproveReject: false };
  }

  if (endDateStr && endDateStr < today) {
    return { status: '만료', canApproveReject: false };
  }

  if (depSign && smSign) {
    return { status: '작업완료', canApproveReject: false };
  }

  if (HasCurrentUserDepSignField(row)) {
    if (IsCurrentUserDepSigned(row)) {
      return { status: '결재완료', canApproveReject: false };
    }
    return { status: '결재요청', canApproveReject: true };
  }

  if (!depSign) {
    return { status: '대기', canApproveReject: false };
  }
  if (depSign && !smSign) {
    if (IsRowSmUser(row, userInfo)) {
      return { status: '승인요청', canApproveReject: true };
    }
    return { status: '대기', canApproveReject: false };
  }

  return { status: '대기', canApproveReject: false };
}
